import { Address, TODO } from '@b2b/types/core';
import angular from 'angular';
import _ from 'lodash';

export class AddressService {
  addressFields = ['streetAddress1', 'streetAddress2', 'city', 'state', 'country', 'zipCode'];
  validateFields = ['streetAddress1', 'city', 'state', 'country', 'zipCode'];
  validateFieldsNoStates = ['streetAddress1', 'city', 'country', 'zipCode'];
  addressMap: Record<number, TODO> = {};

  add(id: number, address: Address, allowDuplicates: boolean) {
    const formattedAddress = this.getFormattedAddress(address);

    if (!this.addressMap[id]) {
      this.addressMap[id] = [];
    }

    if (allowDuplicates) {
      this.addressMap[id].push(formattedAddress, this.addressMap[id]);
    } else if (!this.isDuplicateAddress(formattedAddress, this.addressMap[id])) {
      this.addressMap[id].push(formattedAddress);
    }
  }

  reset(id) {
    if (this.addressMap[id]) {
      delete this.addressMap[id];
    }
  }

  getAddresses(membershipId: number) {
    return this.addressMap[membershipId] || [];
  }

  getById(id: number, addressId: number) {
    const addresses = Array.isArray(this.addressMap[id])
      ? this.addressMap[id].filter((address) => address.id === addressId)
      : [];

    return addresses.length ? addresses[0] : null;
  }

  getByName(id: number, name: string) {
    const addresses = Array.isArray(this.addressMap[id])
      ? this.addressMap[id].filter((address) => address.name === name)
      : [];

    return addresses.length ? addresses[0] : null;
  }

  formatAddress(address: Partial<Address>) {
    return this.addressFields
      .filter((key) => address[key])
      .map((key) => address[key])
      .join(', ');
  }

  getFormattedAddress(address: Partial<Address>) {
    const keys = this.addressFields.slice();
    const values: string[] = [];

    if (address) {
      angular.forEach(keys, (key) => {
        if (address.hasOwnProperty(key) && address[key]) {
          values.push(address[key]);
        }
      });

      address.name = values.join(', ');
    }

    return address as Address;
  }

  getUnFormattedAddress(address: Partial<Address> | null, isNewAddress = false) {
    const keys = this.addressFields.slice();

    if (address) {
      for (const key in address) {
        // delete this or the model's name will get updated.
        if (keys.indexOf(key) < 0) {
          delete address[key];
        }
      }
    }

    if (!address && isNewAddress) {
      address = this.getNewAddress();
    }

    return address;
  }

  getNewAddress() {
    const keys = this.addressFields.slice();
    const newAddress = {};

    angular.forEach(keys, (key) => {
      newAddress[key] = undefined;
    });

    return newAddress as Address;
  }

  checkAddress(data: Partial<Address>) {
    let valid = false;

    for (const key in data) {
      if (!valid && data.hasOwnProperty(key) && data[key]) {
        valid = true;
      }
    }
    return valid;
  }

  isDuplicateAddress(address: Partial<Address>, list: Address[]) {
    const found = list.filter((currentAddress) => {
      if (currentAddress.id && address.id) {
        return currentAddress.id === address.id;
      }

      if (currentAddress.name && address.name) {
        return currentAddress.name === address.name;
      }

      return false;
    });

    return found.length ? found[0] : null;
  }

  createAddress(data: Partial<Address>) {
    const address = this.getNewAddress();

    for (const key in address) {
      if (data.hasOwnProperty(key)) {
        address[key] = angular.copy(data[key]);
      }
    }

    return address;
  }

  getValidators(address: Partial<Address>) {
    switch (address.country) {
      case 'Canada':
      case 'United States':
        return this.validateFields.slice();
      default:
        return this.validateFieldsNoStates.slice();
    }
  }

  validateAddress(address: Partial<Address>) {
    let result = true;
    const keys = this.getValidators(address);

    if (address) {
      angular.forEach(keys, (key) => {
        if (result && address.hasOwnProperty(key) && address[key]) {
          result = !!address[key].length;
        } else {
          result = false;
        }
      });
    }

    return result;
  }

  loadAddressesForMembership(props: { id: number; addresses: Address[] }) {
    this.reset(props.id);

    for (const address of props.addresses) {
      if (this.checkAddress(address)) {
        this.add(props.id, address, false);
      }
    }
  }

  uniqueByName(addresses: Address[]) {
    const list = addresses
      .filter((address) => this.checkAddress(address))
      .map((address) => this.getFormattedAddress(address));

    return _.uniqBy(list, 'name');
  }
}

export const AddressServiceModule = angular
  .module('acme.common.service.address', [])
  .service('AddressService', AddressService).name;
